/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/binding 层；
 * 2. 负责定义“特征输入引用绑定”的统一抽象基类与序列化协议；
 * 3. 所有依赖草图、边、端点、模型子实体的特征，都应通过这里定义的绑定协议接入依赖图与重建系统。
 *
 * 文件职责：
 * 1. 统一约束特征引用绑定的公共能力：序列化、克隆、依赖判定、运行时解析；
 * 2. 定义可持久化保存到命令历史中的绑定载荷结构；
 * 3. 为后续拉伸、旋转、切除、倒角、圆角等特征提供统一扩展入口。
 */

import type * as THREE from 'three';
import type {Viewer} from '../../viewer';

export type {
    SerializedModelTopologyFeatureBinding,
    SerializedFeatureReferenceBinding,
    SerializedSketchProfileFeatureBinding,
    SerializedSketchSubEntityFeatureBinding,
} from '../../../types/index';

/**
 * 特征绑定运行时解析上下文。
 */
export interface FeatureBindingResolveContext {
    /** 当前 Viewer 实例。 */
    viewer: Viewer;
}

/**
 * 草图轮廓绑定运行时解析结果。
 */
export interface ResolvedSketchProfileFeatureBindingValue {
    /** 绑定类型。 */
    kind: 'sketch-profile';
    /** 来源草图 ID。 */
    sketchId: string;
    /** 当前解析得到的轮廓来源对象 ID 列表。 */
    contourSourceObjectIds: string[];
    /** 当前解析得到的轮廓键列表。 */
    contourKeys: string[];
    /** 当前解析得到的区域几何集合。 */
    regionGeometries: ProfileRegionGeometry[];
    /** 当前草图平面快照。 */
    runtimeSketchPlane?: RuntimeSketchPlaneInput;
    /** 当前草图实体快照。 */
    runtimeSketchEntities?: RuntimeSketchEntityInput[];
}

/**
 * 草图子实体绑定运行时解析结果。
 */
export interface ResolvedSketchSubEntityFeatureBindingValue {
    /** 绑定类型。 */
    kind: 'sketch-sub-entity';
    /** 来源草图 ID。 */
    sketchId: string;
    /** 子实体引用。 */
    sourceRef: GeometricSubEntityReference;
    /** 当前命中的场景对象。 */
    object: THREE.Object3D | null;
}

/**
 * 模型拓扑绑定运行时解析结果。
 */
export interface ResolvedModelTopologyFeatureBindingValue {
    /** 绑定类型。 */
    kind: 'model-topology';
    /** 来源实体对象 ID。 */
    bodyObjectId: string;
    /** 当前命中的场景对象。 */
    object: THREE.Object3D | null;
    /** 当前绑定携带的拓扑选择项。 */
    items: SelectionItemRef[];
    /** 当前绑定携带的点类引用。 */
    pointRefs: BlendPointRef[];
}

/**
 * 运行时解析结果联合类型。
 */
export type FeatureBindingResolvedValue =
    | ResolvedSketchProfileFeatureBindingValue
    | ResolvedSketchSubEntityFeatureBindingValue
    | ResolvedModelTopologyFeatureBindingValue
    | {
        /** 拉伸草图点或基准面。 */
        kind: 'extrude-reference';
        /** 独立方向角色。 */
        role: 'extrude-direction1' | 'extrude-direction2';
        /** 已重新解析的目标。 */
        target: ExtrudeEndTarget;
    };

/**
 * 特征引用绑定抽象基类。
 * 说明：
 * 1. 该类不关心具体绑定来源，只约束统一协议；
 * 2. 子类负责解析具体输入来源，并将其转换为特征可消费的运行时值；
 * 3. 依赖图与重建调度器只依赖此抽象，不依赖具体特征实现细节。
 */
export abstract class FeatureReferenceBinding {
    /** 绑定类型。 */
    public abstract readonly kind: SerializedFeatureReferenceBinding['kind'];

    /**
     * 序列化绑定。
     * @returns 可写入命令历史的绑定载荷。
     */
    public abstract serialize(): SerializedFeatureReferenceBinding;

    /**
     * 克隆当前绑定。
     * @returns 绑定副本。
     */
    public abstract clone(): FeatureReferenceBinding;

    /**
     * 判断当前绑定是否依赖指定草图。
     * @param sketchId 草图 ID。
     * @returns 是否依赖。
     */
    public abstract dependsOnSketch(sketchId: string): boolean;

    /**
     * 收集该绑定引用到的模型对象或特征 ID。
     *
     * 设计说明：
     * 1. 特征删除依赖图只消费绑定抽象，不再解析各特征命令参数里的零散字段；
     * 2. 草图绑定默认不引用模型结果体，因此基类回退为空数组；
     * 3. 模型拓扑绑定等子类按自身协议声明依赖，新增绑定类型时只扩展对应绑定类。
     *
     * @returns 模型对象或特征 ID 列表。
     */
    public collectModelReferenceIds(): string[] {
        return [];
    }

    /**
     * 在当前运行时上下文中解析绑定。
     * @param context 解析上下文。
     * @returns 运行时解析结果；无法解析时返回 null。
     */
    public abstract resolve(context: FeatureBindingResolveContext): FeatureBindingResolvedValue | null;
}
